import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";

interface PendingOrder {
  id: string;
  total: number;
  createdAt: number; // timestamp ms
}

interface OnlineOrderAlertProps {
  orders: { id: string; type: string; status: string; total: number; creation_date?: string }[];
}

const CHIME_REPEAT_MS = 5_000; // Repeat chime every 5 seconds for app orders
const URGENT_THRESHOLD_MS = 60 * 1000; // 1 minute

// --- Web Audio API sound generators (no external files) ---
let audioCtx: AudioContext | null = null;

const getAudioCtx = (): AudioContext => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioCtx;
};

const playChime = () => {
  try {
    const ctx = getAudioCtx();
    const now = ctx.currentTime;

    // Two-tone gentle chime
    [523.25, 659.25].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.3, now + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.6);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.15);
      osc.stop(now + i * 0.15 + 0.6);
    });
  } catch (e) {
    console.warn("Audio chime failed:", e);
  }
};

const playUrgentAlarm = () => {
  try {
    const ctx = getAudioCtx();
    const now = ctx.currentTime;

    // Rapid alternating alarm tone
    for (let i = 0; i < 6; i++) {
      const freq = i % 2 === 0 ? 880 : 660;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.2, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.1);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.12);
    }
  } catch (e) {
    console.warn("Audio alarm failed:", e);
  }
};

const OnlineOrderAlert: React.FC<OnlineOrderAlertProps> = ({ orders }) => {
  const history = useHistory();
  const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const knownIds = useRef<Set<string>>(new Set());
  const urgentIntervalRef = useRef<NodeJS.Timer | null>(null);
  const chimeIntervalRef = useRef<NodeJS.Timer | null>(null);
  const [, forceUpdate] = useState(0);

  // Tick every 10s to update "waiting" timers and check urgency
  useEffect(() => {
    const ticker = setInterval(() => forceUpdate((n) => n + 1), 10_000);
    return () => clearInterval(ticker);
  }, []);

  // Detect new orders: online with status "placed" OR pos with status "new"
  useEffect(() => {
    const newOrders = orders.filter(
      (o) =>
        (o.type === "online" && o.status === "placed") ||
        (o.type === "pos" && o.status === "new")
    );

    const newPending: PendingOrder[] = [];
    let hasNew = false;

    newOrders.forEach((o) => {
      if (!knownIds.current.has(o.id) && !dismissedIds.has(o.id)) {
        hasNew = true;
      }
      knownIds.current.add(o.id);

      if (!dismissedIds.has(o.id)) {
        const existingCreatedAt = pendingOrders.find((p) => p.id === o.id)?.createdAt;
        newPending.push({
          id: o.id,
          total: o.total,
          createdAt: existingCreatedAt || (o.creation_date ? new Date(o.creation_date).getTime() : Date.now()),
        });
      }
    });

    setPendingOrders(newPending);

    if (hasNew) {
      playChime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, dismissedIds]);

  // Repeating chime: play chime every 5s while there are pending app orders
  const hasOnlinePending = pendingOrders.length > 0 &&
    orders.some((o) => o.type === "online" && o.status === "placed" && !dismissedIds.has(o.id));

  const startChimeRepeat = useCallback(() => {
    if (chimeIntervalRef.current) return;
    chimeIntervalRef.current = setInterval(playChime, CHIME_REPEAT_MS);
  }, []);

  const stopChimeRepeat = useCallback(() => {
    if (chimeIntervalRef.current) {
      clearInterval(chimeIntervalRef.current);
      chimeIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (hasOnlinePending) {
      startChimeRepeat();
    } else {
      stopChimeRepeat();
    }
    return stopChimeRepeat;
  }, [hasOnlinePending, startChimeRepeat, stopChimeRepeat]);

  // Urgent alarm: if any order has been waiting > 1 min, play alarm every 8s
  const hasUrgent = pendingOrders.some(
    (o) => Date.now() - o.createdAt >= URGENT_THRESHOLD_MS
  );

  const startUrgentAlarm = useCallback(() => {
    if (urgentIntervalRef.current) return;
    stopChimeRepeat(); // Stop chime when escalating to urgent alarm
    playUrgentAlarm();
    urgentIntervalRef.current = setInterval(playUrgentAlarm, 8_000);
  }, [stopChimeRepeat]);

  const stopUrgentAlarm = useCallback(() => {
    if (urgentIntervalRef.current) {
      clearInterval(urgentIntervalRef.current);
      urgentIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (hasUrgent) {
      startUrgentAlarm();
    } else {
      stopUrgentAlarm();
    }
    return stopUrgentAlarm;
  }, [hasUrgent, startUrgentAlarm, stopUrgentAlarm]);

  const dismissOrder = (id: string) => {
    setDismissedIds((prev) => new Set(prev).add(id));
  };

  const goToOrder = (id: string) => {
    history.push(`/order/${id}`);
  };

  if (pendingOrders.length === 0) return null;

  return (
    <div className="online-order-alert-container">
      {pendingOrders.map((order) => {
        const waitMs = Date.now() - order.createdAt;
        const isUrgent = waitMs >= URGENT_THRESHOLD_MS;
        const waitSec = Math.floor(waitMs / 1000);
        const waitMin = Math.floor(waitSec / 60);
        const waitDisplay =
          waitMin > 0 ? `${waitMin}m ${waitSec % 60}s` : `${waitSec}s`;

        return (
          <div
            key={order.id}
            className={`online-order-alert ${isUrgent ? "urgent" : ""}`}
          >
            <div className="alert-icon">
              <i className={`fas ${orders.find((o) => o.id === order.id)?.type === "pos" ? "fa-cash-register" : "fa-shopping-bag"}`} />
            </div>
            <div className="alert-content">
              <div className="alert-title">
                {orders.find((o) => o.id === order.id)?.type === "pos"
                  ? `Nueva orden #${order.id}`
                  : `Pedido en línea #${order.id}`}
              </div>
              <div className="alert-meta">
                <span className="alert-total">
                  ${Number(order.total).toFixed(2)}
                </span>
                <span className="alert-wait">
                  <i className="fas fa-clock" /> {waitDisplay}
                </span>
              </div>
            </div>
            <div className="alert-actions">
              <button
                className="btn btn-sm btn-warning alert-view-btn"
                onClick={() => goToOrder(order.id)}
              >
                Ver pedido
              </button>
              <button
                className="btn btn-sm btn-outline-secondary alert-dismiss-btn"
                onClick={() => dismissOrder(order.id)}
                title="Descartar"
              >
                <i className="fas fa-times" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OnlineOrderAlert;
